import { useState } from "react";
import { motion } from "framer-motion";
import { LockKey, SignIn } from "@phosphor-icons/react";
import { adminLogin, formatApiErrorDetail } from "@/lib/adminApi";

export default function AdminLogin({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const admin = await adminLogin(email.trim().toLowerCase(), password);
      onSuccess(admin);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="min-h-screen bg-altin-ink flex items-center justify-center px-6 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #C5A059 1px, transparent 0)",
          backgroundSize: "40px 40px",
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative w-full max-w-md"
      >
        <div className="text-center mb-10">
          <img
            src="/altin-logo-sm.png"
            alt="Altin Link"
            className="h-20 w-auto object-contain mx-auto"
          />
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="gold-divider" />
            <span className="overline">Admin Access</span>
            <span className="gold-divider" />
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="bg-white p-8 md:p-10 border border-altin-border"
        >
          <div className="w-12 h-12 grid place-items-center border border-altin-gold text-altin-gold mb-8">
            <LockKey size={24} weight="light" />
          </div>
          <h1 className="font-serif-display text-3xl text-altin-ink tracking-tight">
            Dashboard Login
          </h1>
          <p className="mt-2 text-sm text-altin-muted">
            Authorised personnel only.
          </p>

          <div className="mt-8 space-y-6">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-altin-muted mb-2">
                Email
              </label>
              <input
                type="email"
                data-testid="admin-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-altin-border focus:border-altin-gold focus:outline-none py-3 text-altin-ink transition-colors"
                autoComplete="username"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-altin-muted mb-2">
                Password
              </label>
              <input
                type="password"
                data-testid="admin-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border-b border-altin-border focus:border-altin-gold focus:outline-none py-3 text-altin-ink transition-colors"
                autoComplete="current-password"
              />
            </div>
          </div>

          {error && (
            <p data-testid="admin-login-error" className="mt-6 text-sm text-destructive">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="mt-10 w-full inline-flex items-center justify-center gap-3 bg-altin-ink text-white px-8 py-4 text-xs tracking-[0.22em] uppercase hover:bg-altin-gold transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? "Signing in..." : "Sign In"}
            <SignIn size={18} weight="light" />
          </button>
        </form>
      </motion.div>
    </div>
  );
}
